import React, { useCallback, useMemo, useReducer } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { Button } from '@xsolla/ui-core';
import { ArrowLeft } from '@xsolla/ui-icons';
import { useModal } from '@xsolla/ui-modal';

import { LoginProjectConfiguration, updateUserGroups } from '../../../api';
import { Group, User, UserSocialProfile } from '../../../api/users/types';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteUserParams } from '../../../routes/types';
import { HorizontalCenterColumnBlock } from '../../../styled';
import { FormFooter } from '../../ui';
import AdditionalInfo from './AdditionalInfo';
import ChangeEmail from './ChangeEmail';
import ChangePhone from './ChangePhone';
import DeleteUserModal from './DeleteUsersModal';
import GeneralInfo from './GeneralInfo';
import { addGroup, deleteGroup } from './mutators';
import { init, initialState, userReducer } from './reducer';
import SelectUserGroup from './SelectGroup';
import { SettingsBlock } from './SettingsBlock/SettingsBlock';
import { BoldWrapper, DeleteUserSubtitle, FormWrapper, SettingsHeaderWrapper } from './style';
import { ActionType, FormValues } from './types';
import { UserSettingsValidator } from './validator';

type Props = RouteComponentProps & {
  userData: User;
  groups: Group[];
  currentProject: LoginProjectConfiguration;
  userSocialProfile: UserSocialProfile[] | [];
  onSocialProfileRemoved: (providerName: string) => void;
};

const UserSettingsForm = ({
  userData,
  groups,
  currentProject,
  match,
  history,
  userSocialProfile,
  onSocialProfileRemoved
}: Props): JSX.Element => {
  const { usersPath } = usePaths();
  const intl = useIntl();
  const { projectId, userId } = match.params as RouteUserParams;

  const [state, dispatch] = useReducer(userReducer, initialState, () =>
    init({ user: userData, groups, configuration: currentProject })
  );

  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const userGroups = useMemo(() => {
    const { user } = state;

    if (user && user.groups) {
      return user.groups.reduce((acc, item) => (!item.is_default ? [...acc, item.id] : acc), []);
    }

    return [];
  }, [state.user.groups]);

  const groupsChanged = useCallback(
    (values: Array<number>) => {
      values.sort();
      userGroups.sort();
      return values.toString() === userGroups.toString();
    },
    [userGroups]
  );

  const backToUserList = useCallback(() => {
    history.push({ pathname: usersPath(projectId) });
  }, [projectId]);

  const [onOpenModal, onCloseModal] = useModal(
    () => (
      <DeleteUserModal
        userId={userId}
        projectId={projectId}
        onClose={onCloseModal}
        onDelete={backToUserList}
      />
    ),
    []
  );

  const onSubmit = async (values: FormValues) => {
    try {
      dispatch({ type: ActionType.submitStart });
      const response: Group[] = await updateUserGroups(projectId, userId, {
        user_groups: values.user_groups.filter((id) => !!id)
      });

      showSuccessNotify();
      dispatch({ type: ActionType.submitSuccess, payload: response });
    } catch (err) {
      showErrorNotify();
      dispatch({ type: ActionType.submitFail, payload: 'asd' });
    }
  };

  return (
    <HorizontalCenterColumnBlock>
      <Form
        onSubmit={onSubmit}
        validate={UserSettingsValidator}
        initialValues={{
          user_groups: userGroups,
          country:
            userData?.country &&
            intl.formatMessage({ id: `form.select.country-name.${userData?.country}` }),
          age: userData?.birthday
        }}
        mutators={{ addGroup, deleteGroup }}>
        {({ handleSubmit, form, errors, submitFailed, values: { user_groups, country, age } }) => {
          return (
            <FormWrapper>
              <SettingsHeaderWrapper>
                <Button
                  look="link"
                  palette="muted"
                  onClick={backToUserList}
                  data-test-id="user-go-back">
                  <ArrowLeft />
                  <span>
                    {intl.formatMessage(
                      { id: 'users.settings.back' },
                      {
                        b: (chunk: string) => <BoldWrapper>{chunk}</BoldWrapper>
                      }
                    )}
                  </span>
                </Button>
              </SettingsHeaderWrapper>

              <GeneralInfo
                state={state}
                dispatch={dispatch}
                projectId={projectId}
                projectStorage={currentProject.users_storage}
                userSocialProfile={userSocialProfile}
                onSocialProfileRemoved={onSocialProfileRemoved}
              />

              <SettingsBlock label={intl.messages['users.settings.group.label']} withBorder>
                <SelectUserGroup
                  name="user_groups"
                  addInput={form.mutators.addGroup}
                  deleteInput={form.mutators.deleteGroup}
                  groups={groups}
                  values={user_groups}
                  submitFailed={submitFailed}
                  errors={errors}
                  label={intl.messages['users.settings.group.input-label']}
                />
              </SettingsBlock>
              <SettingsBlock label={intl.messages['users.settings.contact-info.label']} withBorder>
                <ChangeEmail state={state} projectId={projectId} dispatch={dispatch} />

                <ChangePhone state={state} projectId={projectId} dispatch={dispatch} />
              </SettingsBlock>

              <AdditionalInfo state={state} />

              <SettingsBlock>
                <Button
                  type="button"
                  onClick={onOpenModal}
                  look="link"
                  palette="danger"
                  size="md"
                  data-test-id="user-delete">
                  {intl.messages['users.settings.delete-user.button']}
                </Button>
                <DeleteUserSubtitle>
                  {intl.messages['users.settings.delete-user.subtitle']}
                </DeleteUserSubtitle>
              </SettingsBlock>

              <FormFooter
                onCancel={backToUserList}
                fetching={state.saveDataFetching}
                onSubmit={handleSubmit}
                disabled={groupsChanged(user_groups)}
                error={!!state.submitError}
              />
            </FormWrapper>
          );
        }}
      </Form>
    </HorizontalCenterColumnBlock>
  );
};

export default withRouter(UserSettingsForm);
